import { ApolloError, ApolloQueryResult } from '@apollo/client';
import { GraphQLError } from 'graphql/error/GraphQLError';
import { FetchResult } from '@apollo/client/link/core';
import { GraphQLFormattedError } from 'graphql';

interface GqlError extends Omit<Partial<GraphQLError>, 'extensions'> {
  extensions?: { message: string; status: number; code?: string };
  message: string;
  code?: string;
}

export class GqlResponseError {
  private readonly statusCode?: number;

  private errorsArray: GqlError[] = [];

  public operationName: string = '';

  constructor(
    errors:
      | GqlError[]
      | readonly GraphQLFormattedError<Record<string, any>>[]
      | ApolloError
      | ReadonlyArray<GraphQLError>
      | undefined,
    operationName?: string,
    status?: number
  ) {
    if (isApolloError(errors)) {
      this.errorsArray = errors.graphQLErrors as GqlError[];
    } else {
      this.errorsArray = errors as GqlError[];
    }
    this.operationName = operationName ?? this.errorsArray?.[0]?.path?.[0]?.toString() ?? '';
    this.statusCode = this.errorsArray?.[0]?.extensions?.status ?? status;
  }

  get message(): string {
    return this.errorsArray?.[0]?.extensions?.message ?? this.errorsArray?.[0]?.message ?? 'UNDEFINED';
  }

  get code(): string {
    return this.errorsArray?.[0]?.extensions?.code ?? this.errorsArray?.[0]?.code ?? 'UNCLASSIFIED';
  }

  get errors(): GqlError[] {
    return this.errorsArray;
  }

  get isError(): boolean {
    return !!this.errorsArray?.length;
  }

  get status(): number | undefined {
    return this.statusCode;
  }

  toString(): string {
    return this.message;
  }
}

export function isGqlResponseError(error: unknown): error is GqlResponseError {
  if (!error || typeof error !== 'object') {
    return false;
  }
  return 'isError' in error;
}

function isApolloError<T>(
  result: ApolloQueryResult<T> | FetchResult<T> | ApolloError | unknown
): result is ApolloError {
  if (typeof result !== 'object' || result === null) {
    return false;
  }
  return 'graphQLErrors' in result;
}
